import { FeatureFlag } from './featureFlags';

/**
 * One entry per section of the Internal Tools tab.
 *
 * Unlike {@link ../constants/adminSections}, there are no per-section permission
 * slugs: Internal Tools is gated wholesale — the page refuses anyone who is not a
 * Strawhat `admin:super-user` before it renders a tab strip at all — so a section
 * either exists everywhere the page does, or is hidden by the deploy stage or by
 * a feature flag on the caller's own organization.
 */
export interface InternalToolsSectionDefinition {
  /** Stable id, used as the tab value and the `?section=` query parameter. */
  id: string;
  label: string;
  /**
   * Sections that must not exist in production. Today only demo agency setup,
   * whose API is unmounted in prod as well.
   */
  nonProdOnly: boolean;
  /**
   * A WorkOS feature flag the caller's organization must carry, for a section
   * whose API is gated by the same flag. Omitted where the page's own
   * Strawhat gate is the whole answer.
   */
  featureFlag?: string;
}

export const INTERNAL_TOOLS_SECTIONS: readonly InternalToolsSectionDefinition[] =
  [
    {
      // First, and so the tab the page opens on. The two sections that used
      // to lead are each conditional -- Demo Sandboxes is non-prod only and
      // Agency Data Fill is behind a flag -- so the landing tab depended on
      // the stage and on who was looking.
      id: 'agencies-users',
      label: 'Agencies & Users',
      nonProdOnly: false,
    },
    {
      id: 'demo-environments',
      label: 'Demo Sandboxes',
      nonProdOnly: true,
    },
    {
      id: 'agency-data-fill',
      label: 'Agency Data Fill',
      // Production too: the tab reads what the nightly sync did for every
      // agency, which is a production question before it is a demo one.
      nonProdOnly: false,
      featureFlag: FeatureFlag.KINNSER_AGENCY_SYNC_UI,
    },
    {
      id: 'product-updates',
      label: 'Product Updates',
      nonProdOnly: false,
    },
    {
      id: 'stedi',
      label: 'Stedi',
      nonProdOnly: false,
    },
    {
      id: 'evv',
      label: 'EVV',
      nonProdOnly: false,
    },
  ];

/**
 * The sections this deploy stage and this caller get, in tab order.
 *
 * Takes the answers rather than asking for them: the caller passes the stage
 * predicate and its own flags, so this stays a pure function of its arguments,
 * testable without stubbing the deploy-stage module or the auth store.
 *
 * ``flags`` is required rather than defaulted. A default would have to be
 * "every flag" or "none", and either one silently decides a gate for a caller
 * that forgot to pass anything.
 */
export function visibleInternalToolsSections(
  isNonProd: boolean,
  flags: readonly string[]
): InternalToolsSectionDefinition[] {
  return INTERNAL_TOOLS_SECTIONS.filter(
    section =>
      (!section.nonProdOnly || isNonProd) &&
      (!section.featureFlag || flags.includes(section.featureFlag))
  );
}
